/**
 * Календарные выходы техкарты v2 (ADR-071 D9, D10) — слой над
 * `/planning/tech-cards/{id}/monthly|procurement` и
 * `/planning/scenarios/{id}/monthly|procurement`.
 *
 * Что обещает пользователю:
 *   • помесячный свод затрат по статьям — 24 месяца (год НЗП + год урожая),
 *     затраты размазаны по календарным дням агроокна;
 *   • список закупок ТМЦ — позиция × месяц, закупка целиком ЗА МЕСЯЦ ДО
 *     начала работ; правило видимое — печатаем его текстом, а не прячем;
 *   • деньги закупок — только с правом на бюджетный справочник: без него
 *     количества остаются, суммы честно скрыты.
 *
 * Язык — экономиста и снабженца: «свод по месяцам», «закупки», «по правилу»,
 * никакие basis/view наружу не отдаются.
 */
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { techCardKeys } from '@/lib/planning/queryKeys';
import type { CostArticle } from '@/lib/planning/techCards';

// ── Типы ответа ──────────────────────────────────────────────────────────────

export type MonthlyBasis = 'harvest' | 'cash';
export type ProcurementView = 'qty' | 'money' | 'both';

export interface MonthlyRow {
  article: CostArticle;
  months: Record<string, number>;
  /** Деньги строк без агроокна — отдельная колонка, не «первый месяц». */
  unscheduled: number;
  total: number;
}

export interface MonthlyReport {
  horizon: string[];
  basis: MonthlyBasis;
  rows: MonthlyRow[];
  month_totals: Record<string, number>;
  total: number;
  wip: {
    incoming: number;
    outgoing: number | null;
    cash_in_season: number;
    outgoing_status: string;
    outgoing_source: { scenario_id: string; name: string; season_year: number } | null;
  };
  gaps: string[];
}

export interface ProcurementCell {
  qty: number | null;
  amount: number | null;
}

export interface ProcurementRow {
  budget_item_id: string;
  name: string | null;
  section: string | null;
  unit_id: string | null;
  /** Символ единицы количества («кг», «л», «т»). */
  unit: string | null;
  months: Record<string, ProcurementCell>;
  unscheduled: ProcurementCell | null;
  month_needed_rule: string;
  month_needed_rule_label: string;
  total_qty: number | null;
  total_amount: number | null;
}

export interface ProcurementReport {
  basis: MonthlyBasis;
  view: ProcurementView;
  prices_visible: boolean;
  month_needed_rule: string;
  month_needed_rule_label: string;
  rows: ProcurementRow[];
  gaps: string[];
}

// ── Чтение ───────────────────────────────────────────────────────────────────

interface CardScope {
  kind: 'card';
  cardId: string;
  scenarioId: string;
}

interface ScenarioScope {
  kind: 'scenario';
  scenarioId: string;
}

/** Уровень отчёта: одна карта в контексте варианта либо весь вариант плана. */
export type ReportScope = CardScope | ScenarioScope;

function monthlyUrl(scope: ReportScope, basis: MonthlyBasis): string {
  if (scope.kind === 'card') {
    return `/api/v1/planning/tech-cards/${scope.cardId}/monthly?scenario_id=${scope.scenarioId}&basis=${basis}`;
  }
  return `/api/v1/planning/scenarios/${scope.scenarioId}/monthly?basis=${basis}`;
}

function procurementUrl(scope: ReportScope, basis: MonthlyBasis, view: ProcurementView): string {
  if (scope.kind === 'card') {
    return `/api/v1/planning/tech-cards/${scope.cardId}/procurement?scenario_id=${scope.scenarioId}&basis=${basis}&view=${view}`;
  }
  return `/api/v1/planning/scenarios/${scope.scenarioId}/procurement?basis=${basis}&view=${view}`;
}

export function useMonthlyReport(scope: ReportScope | null, basis: MonthlyBasis) {
  return useQuery<MonthlyReport, Error>({
    queryKey: [
      ...techCardKeys.all(),
      'monthly',
      scope?.kind ?? 'none',
      scope?.kind === 'card' ? scope.cardId : '',
      scope?.scenarioId ?? '',
      basis,
    ],
    queryFn: () => api.get<MonthlyReport>(monthlyUrl(scope as ReportScope, basis)),
    enabled: !!scope,
    staleTime: 30_000,
  });
}

export function useProcurementReport(
  scope: ReportScope | null,
  basis: MonthlyBasis,
  view: ProcurementView,
) {
  return useQuery<ProcurementReport, Error>({
    queryKey: [
      ...techCardKeys.all(),
      'procurement',
      scope?.kind ?? 'none',
      scope?.kind === 'card' ? scope.cardId : '',
      scope?.scenarioId ?? '',
      basis,
      view,
    ],
    queryFn: () => api.get<ProcurementReport>(procurementUrl(scope as ReportScope, basis, view)),
    enabled: !!scope,
    staleTime: 30_000,
  });
}

// ── Чистые помощники таблиц (покрыты vitest) ────────────────────────────────

const MONTH_SHORT = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];

/** «2026-09» → «сен 26». Ключ месяца наружу не показываем. */
export function monthLabel(key: string): string {
  const [year, month] = key.split('-');
  const idx = Number(month) - 1;
  if (!year || !month || Number.isNaN(idx) || idx < 0 || idx > 11) return key;
  return `${MONTH_SHORT[idx]} ${year.slice(2)}`;
}

/**
 * Колонки таблицы — только месяцы, где есть хоть одна цифра, в календарном
 * порядке. Пустые 20 колонок из 24-месячного горизонта раздували бы таблицу
 * и прятали деньги за горизонтальным скроллом.
 */
export function visibleMonths(rows: { months: Record<string, unknown> }[]): string[] {
  const keys = new Set<string>();
  for (const row of rows) for (const key of Object.keys(row.months)) keys.add(key);
  return [...keys].sort();
}

/** Строки свода с деньгами — нулевые статьи таблицу не засоряют. */
export function nonEmptyRows(rows: MonthlyRow[]): MonthlyRow[] {
  return rows.filter((row) => row.total !== 0 || row.unscheduled !== 0);
}

/** Есть ли в отчёте закупок колонка «без срока» (строки без агроокна). */
export function hasUnscheduled(rows: { unscheduled: unknown }[]): boolean {
  return rows.some((row) => {
    const cell = row.unscheduled;
    if (cell === null || cell === undefined) return false;
    if (typeof cell === 'number') return cell !== 0;
    const { qty, amount } = cell as ProcurementCell;
    return !!qty || !!amount;
  });
}
